import React, { FunctionComponent, useState, useCallback, useMemo, Dispatch, SetStateAction } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { addImage } from '@/static/images';
import { Image } from '@/containers/ActionUniqueEvent/utils';
import { ImageActionButton, ImageGalleryCropper } from './components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImage } from '@fortawesome/free-regular-svg-icons';
import { InputMultipleFile } from '../Input';

const reorder = (list: Array<Image>, startIndex: number, endIndex: number) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

type ImageGalleryProps = {
  images: Image[];
  setImages: Dispatch<SetStateAction<Image[]>>;
  title: JSX.Element;
};

export const ImageGallery: FunctionComponent<ImageGalleryProps> = ({ images, setImages, title }) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState<Image | undefined>();

  const nextImageToFill = useMemo(() => images.findIndex((image) => !image.url), [images]);

  const onDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination) return;
      const sourceIdx = result.source.index;
      const destIdx = result.destination.index;
      if (!images[sourceIdx].url) return;
      const lastFilledIndex = images.reduce((acc, img, idx) => (img.url ? idx : acc), -1);
      if (destIdx > lastFilledIndex + 1) return;
      setImages(reorder(images, sourceIdx, destIdx));
    },
    [images, setImages]
  );

  const handleCrop = (image: Image) => {
    setShowModal(true);
    setSelectedImage(image);
  };

  const handleDelete = (imageToDelete: Image) => {
    setImages((prevImages) => {
      const deletedIndex = prevImages.findIndex((img) => img.id === imageToDelete.id);
      const newImages = [...prevImages];
      newImages[deletedIndex] = { id: imageToDelete.id };
      return newImages;
    });
  };

  const handleFile = (newFiles: File[]) => {
    if (!newFiles?.length) return;
    setImages((prevImages) => {
      const updatedImages = [...prevImages];
      let fileIndex = 0;
      for (let i = 0; i < updatedImages.length && fileIndex < newFiles.length; i++) {
        if (!updatedImages[i].url) {
          updatedImages[i] = {
            id: updatedImages[i].id,
            url: URL.createObjectURL(newFiles[fileIndex]),
            blob: newFiles[fileIndex]
          };
          fileIndex++;
        }
      }
      return updatedImages;
    });
  };

  return (
    <div className="w-full rounded-lg">
      {showModal && selectedImage && (
        <ImageGalleryCropper
          selectedImage={selectedImage}
          onClose={() => setShowModal(false)}
          setImages={setImages}
          images={images}
        />
      )}
      <div className="mb-6">{title}</div>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable direction="horizontal" droppableId="droppable">
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className={`flex flex-wrap gap-4 p-2 min-h-[180px] rounded-lg transition-all duration-200 ${
                snapshot.isDraggingOver ? 'bg-gray-100 border-2 border-dashed border-gray-100' : ''
              }`}
            >
              <div className="relative group h-36 aspect-square bg-white flex flex-col items-center justify-center border-2 border-dashed border-gray-300 hover:border-blue rounded-lg transition-all duration-200 hover:shadow-md">
                <InputMultipleFile
                  reference="add-image"
                  multiple
                  disabled={nextImageToFill === -1}
                  handleFile={handleFile}
                >
                  <div className="opacity-60 group-hover:opacity-100 flex flex-col items-center space-y-2 group-hover:scale-105 transition-transform duration-200">
                    <img src={addImage} className="w-8 h-8" />
                    <p className="text-sm font-medium group-hover:text-blue text-[#0072FB]/70">
                      Agregar imágenes
                    </p>
                  </div>
                </InputMultipleFile>
              </div>
              {images.map((item, index) => (
                <Draggable key={item.id} draggableId={item.id} index={index} isDragDisabled={!item.url}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      style={{
                        ...provided.draggableProps.style
                      }}
                      className={`relative group h-36 aspect-square rounded-lg overflow-hidden transition-all duration-200 ${
                        snapshot.isDragging
                          ? 'scale-105 shadow-xl ring-2 ring-blue ring-offset-2'
                          : 'shadow-md hover:shadow-lg'
                      } ${!item.url ? 'bg-gray-100' : 'bg-white'}`}
                    >
                      {item.url ? (
                        <>
                          <div
                            className="h-36 aspect-square inset-0 bg-contain bg-no-repeat bg-center"
                            style={{ backgroundImage: `url(${item.url})` }}
                          />
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-200" />
                          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                            <ImageActionButton
                              handleCrop={() => handleCrop(item)}
                              handleDelete={() => handleDelete(item)}
                            />
                          </div>
                        </>
                      ) : (
                        <div className="h-full w-full flex items-center justify-center">
                          <FontAwesomeIcon
                            icon={faImage}
                            width="40px"
                            height="40px"
                            className="text-gray-400"
                          />
                        </div>
                      )}
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
};

export default ImageGallery;
